import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Lightbulb, UserRound, Layers, Info } from "lucide-react";
import { SERVICES, serviceBySlug } from "@/data/services";
import { ENGAGEMENT_MODELS, PROCESS } from "@/data/content";
import { FAQ_GROUPS } from "@/data/faqs";
import { PageHero, SectionHead, CtaBand, FaqList, BulletList } from "@/components/Blocks";
import { Cta } from "@/components/Cta";
import { Reveal, Stagger, itemVariants } from "@/components/motion";
import ContactForm from "@/components/forms/ContactForm";
import { useSeo } from "@/lib/seo";
import NotFound from "@/pages/NotFound";

const AUDIENCES = [
  [Lightbulb, "Startup founders", "MVP development, technical direction, product strategy, and AI implementation."],
  [UserRound, "Non-technical founders", "A partner to translate the idea into requirements, architecture, and a working product."],
  [Layers, "Startups with existing products", "Architecture, engineering leadership, AI integration, scalability, and security."],
];

const CHALLENGES = [
  "“We have an idea but don't know what to build first.”", "“Our prototype can't handle real users.”",
  "“We need technical leadership but can't hire a CTO yet.”", "“We want AI in the product but don't know where it fits.”",
  "“Releases keep breaking things.”", "“Quotes from agencies are wildly different.”",
];

const SERVICE_TO_OPTION = {
  "product-discovery": "Product discovery", "mvp-development": "MVP development", "ai-engineering": "AI engineering",
  "fractional-cto": "Fractional CTO", "architecture-cloud": "Architecture and cloud", "product-rescue": "Product modernization",
};

export function ServicesPage() {
  useSeo("Services", "Product discovery, MVP & SaaS development, AI engineering, fractional CTO, architecture & cloud, and product modernization for startups.");
  return (
    <>
      <PageHero testId="services-hero" eyebrow="Services" title={["Your product vision.", "Our engineering execution."]}
        intro="We help founders shape, build, launch, and strengthen software products — with senior technical ownership from first conversation to production.">
        <Cta to="/contact" size="lg" testId="services-hero-cta">Discuss Your Product</Cta>
        <Cta to="/engagement-models" variant="secondary" size="lg">Engagement Models</Cta>
      </PageHero>
      <section className="border-t border-si-line py-20 sm:py-28">
        <div className="wrap">
          <SectionHead eyebrow="Who we help" title="Built for founders at every stage." />
          <Stagger className="mt-14 grid gap-5 md:grid-cols-3">
            {AUDIENCES.map(([Icon, t, d]) => (
              <motion.div key={t} variants={itemVariants} className="panel p-8">
                <Icon className="h-7 w-7 text-si-cyan" />
                <h3 className="mt-8 text-2xl font-bold tracking-tight">{t}</h3>
                <p className="mt-3 text-base leading-relaxed text-si-muted">{d}</p>
              </motion.div>
            ))}
          </Stagger>
        </div>
      </section>
      <section className="border-t border-si-line bg-si-bg2 py-20 sm:py-28" data-testid="service-categories">
        <div className="wrap">
          <SectionHead eyebrow="Service categories" title="Six ways we can help." />
          <div className="mt-14 divide-y divide-si-line border-y border-si-line">
            {SERVICES.map((s, i) => (
              <Reveal key={s.slug} delay={i * 0.04}>
                <Link to={`/services/${s.slug}`} data-testid={`service-row-${s.slug}`}
                  className="group grid items-center gap-4 py-8 transition-colors sm:grid-cols-12 sm:gap-8">
                  <span className="font-mono text-sm text-si-cyan sm:col-span-1">0{i + 1}</span>
                  <h3 className="text-2xl font-bold tracking-tight transition-transform duration-300 group-hover:translate-x-2 sm:col-span-5 sm:text-4xl">{s.short}</h3>
                  <p className="text-base text-si-muted sm:col-span-5">{s.title}</p>
                  <ArrowRight className="hidden h-6 w-6 justify-self-end text-si-muted transition-[transform,color] group-hover:translate-x-1 group-hover:text-si-cyan sm:col-span-1 sm:block" />
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <section className="py-20 sm:py-28">
        <div className="wrap grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5"><SectionHead eyebrow="Common startup challenges" title="Sound familiar?" intro="These are the situations founders most often bring to us." /></div>
          <Stagger className="grid gap-4 sm:grid-cols-2 lg:col-span-7">
            {CHALLENGES.map((c) => <motion.p key={c} variants={itemVariants} className="panel p-6 text-lg font-medium leading-snug">{c}</motion.p>)}
          </Stagger>
        </div>
      </section>
      <section className="border-t border-si-line bg-si-bg2 py-20 sm:py-28">
        <div className="wrap grid gap-12 lg:grid-cols-2">
          <div>
            <SectionHead eyebrow="Engagement models" title="Flexible ways to work together." />
            <ul className="mt-10 space-y-4">{ENGAGEMENT_MODELS.map(([t, d]) => <li key={t} className="border-l-2 border-si-cyan/60 pl-5"><p className="font-bold">{t}</p><p className="text-sm text-si-muted">{d}</p></li>)}</ul>
            <div className="mt-8"><Cta to="/engagement-models" variant="ghost">All engagement models</Cta></div>
          </div>
          <div>
            <SectionHead eyebrow="Delivery process" title="How a project runs." />
            <ol className="mt-10 space-y-4">{PROCESS.map(([t, d], i) => <li key={t} className="flex gap-4"><span className="font-mono text-sm text-si-cyan">0{i + 1}</span><span><span className="font-bold">{t}</span><span className="block text-sm text-si-muted">{d}</span></span></li>)}</ol>
            <div className="mt-8"><Cta to="/how-we-build" variant="ghost">See How We Work</Cta></div>
          </div>
        </div>
      </section>
      <section className="py-20 sm:py-28">
        <div className="wrap grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4"><SectionHead eyebrow="FAQ" title="Service questions." /></div>
          <div className="lg:col-span-8"><FaqList items={[...FAQ_GROUPS[0].items.slice(1), ...FAQ_GROUPS[2].items]} testId="services-faq" /></div>
        </div>
      </section>
      <CtaBand testId="services-cta" />
    </>
  );
}

export function ServiceDetail() {
  const { slug } = useParams();
  const s = serviceBySlug(slug);
  useSeo(s ? s.short : "Not found", s?.intro);
  if (!s) return <NotFound />;
  return (
    <>
      <PageHero testId={`service-hero-${s.slug}`} eyebrow={s.eyebrow} title={s.title} intro={s.intro}>
        <Cta href="#inquiry" size="lg" testId="service-hero-cta">{s.cta}</Cta>
        <Cta to="/how-we-build" variant="secondary" size="lg">How We Build</Cta>
      </PageHero>
      <section className="border-t border-si-line py-20 sm:py-28">
        <div className="wrap">
          <SectionHead eyebrow="What it covers" title="What a typical engagement can include." />
          <Stagger className="mt-14 grid gap-px overflow-hidden rounded-3xl border border-si-line bg-si-line sm:grid-cols-2 lg:grid-cols-4">
            {s.covers.map(([t, d], i) => (
              <motion.div key={t} variants={itemVariants} className="group bg-si-bg p-7 transition-colors duration-300 hover:bg-si-surface">
                <span className="font-mono text-xs text-si-cyan">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="mt-6 text-lg font-bold leading-snug">{t}</h3>
                <p className="mt-3 text-[15px] leading-relaxed text-si-muted">{d}</p>
              </motion.div>
            ))}
          </Stagger>
        </div>
      </section>
      <section className="border-t border-si-line bg-si-bg2 py-20 sm:py-28">
        <div className="wrap grid gap-10 lg:grid-cols-3">
          {[["When it makes sense", s.when], ["What shapes scope & cost", s.factors], ["What we need from you", s.founder]].map(([t, items]) => (
            <Reveal key={t} className="panel p-8"><h3 className="mb-6 text-2xl font-bold tracking-tight">{t}</h3><BulletList items={items} /></Reveal>
          ))}
        </div>
        {s.note && (
          <div className="wrap mt-10">
            <Reveal className="flex gap-4 rounded-2xl border border-si-cyan/30 bg-si-cyan/5 p-6 text-base leading-relaxed text-si-text"><Info className="mt-0.5 h-5 w-5 shrink-0 text-si-cyan" />{s.note}</Reveal>
          </div>
        )}
      </section>
      <section id="inquiry" className="scroll-mt-24 py-20 sm:py-28">
        <div className="wrap grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <SectionHead eyebrow="Start here" title={s.cta + "."} intro="Tell us about your product and where you are today. We'll reply with a practical next step." />
            <div className="mt-10 space-y-3">
              <p className="eyebrow !text-si-muted">Related services</p>
              {SERVICES.filter((x) => x.slug !== s.slug).slice(0, 3).map((x) => (
                <Link key={x.slug} to={`/services/${x.slug}`} className="group flex items-center justify-between border-b border-si-line py-3 font-semibold hover:text-si-cyan">{x.short}<ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></Link>
              ))}
            </div>
          </div>
          <div className="panel p-6 sm:p-10 lg:col-span-7"><ContactForm defaultService={SERVICE_TO_OPTION[s.slug]} /></div>
        </div>
      </section>
    </>
  );
}
